import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";

/*
  Filename:    Register.js
  Description: This page is the initial registration page for volunteers - Step 1 of 2.
*/

const Register = () => {
  useEffect(() => {
    // Code to run only on first page load
    window.scrollTo(0, 0); // scroll to top of page
  }, []);

  const navigate = useNavigate();

  const [clientInfo, setClientInfo] = useState({
    firstName: "",
    middleName: "",
    lastName: "",
    DOB: "",
  });

  const [userInfo, setUserInfo] = useState({
    email: "",
    userName: "",
    password: "",
  });

  const [confirmPassword, setConfirmPassword] = useState({
    confirmPassword: "",
  });

  const [errors, setErrors] = useState([]);

  const validateInputs = () => {
    const nameReg = /^[A-Za-z'-]+$/;
    const opNameReg = /^[A-Za-z' -]*$/;
    const dateReg = /\b(\d{4})-(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])\b/;
    const emailReg = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    const userNameReg = /^[a-zA-Z0-9._-]{3,15}$/;
    const passReg =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*(),.?":{}|<>])[A-Za-z\d!@#$%^&*(),.?":{}|<>]{8,}$/;

    let newErrors = [];

    // Client information validation
    if (!nameReg.test(clientInfo.firstName)) {
      newErrors.push("First name must only contain letters, (') or (-)");
    }
    if (!opNameReg.test(clientInfo.middleName)) {
      newErrors.push("Middle name must only contain letters, (') or (-)");
    }
    if (!nameReg.test(clientInfo.lastName)) {
      newErrors.push("Last name must only contain letters, (') or (-)");
    }
    if (!dateReg.test(clientInfo.DOB)) {
      newErrors.push("You must enter a valid date");
    }

    // User information validation
    if (!emailReg.test(userInfo.email)) {
      newErrors.push("Please enter a valid email address");
    }
    if (!userNameReg.test(userInfo.userName)) {
      newErrors.push(
        "Username must be between 3 and 15 characters and only include alphanumeric, (.), (_) or (-)"
      );
    }
    if (!passReg.test(userInfo.password)) {
      newErrors.push(
        `Password must be at least 8 characters and must include at least a capital and lowercase letter, a number, and a special character consisting of !@#$%^&*(),.?":{}|<></>`
      );
    } else {
      if (userInfo.password !== confirmPassword.confirmPassword) {
        newErrors.push("Password and Confirm Password must be the same");
      }
    }

    setErrors(newErrors);
    return newErrors.length === 0;
  };

  const handleChange = (event) => {
    const { dataset, name, value } = event.target;

    if (dataset.type === "client") {
      setClientInfo({
        ...clientInfo,
        [name]: value,
      });
    }
    if (dataset.type === "user") {
      setUserInfo({
        ...userInfo,
        [name]: value,
      });
    }
    if (dataset.type === "confirmPassword") {
      setConfirmPassword({
        ...confirmPassword,
        [name]: value,
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const isValid = validateInputs();

    if (isValid) {
      let params = {
        client: clientInfo,
        user: userInfo,
      };

      // Instead of calling the API, just store the registration data in localStorage
      localStorage.setItem('registrationData', JSON.stringify({
        client: clientInfo,
        user: userInfo
      }));
      navigate("/completeregistration");
    }
  };



  return (
    <React.Fragment>
      <div className="col-12 d-flex justify-content-center align-items-center min-vh-100">
        <div className="card col-sm-12 col-md-10 col-lg-8">
          <div className="card-body">
            <div className="col-12 d-flex justify-content-center align-items-center">
              <img
                src="images/BridgingHopeWord.svg"
                className="img-fluid col-6"
                alt="logo"
              />
            </div>

            {/* Progress indicator */}
            <div className="mt-4 mb-4">
              <div className="d-flex justify-content-between align-items-center">
                <div className="text-center">
                  <div className="rounded-circle bg-primary text-white d-inline-flex align-items-center justify-content-center" style={{ width: '40px', height: '40px' }}>
                    1
                  </div>
                  <div className="mt-2">
                    <small>Basic Info</small>
                  </div>
                </div>
                <div className="flex-grow-1 mx-3">
                  <hr className="border-2" />
                </div>
                <div className="text-center">
                  <div className="rounded-circle bg-secondary text-white d-inline-flex align-items-center justify-content-center" style={{ width: '40px', height: '40px' }}>
                    2
                  </div>
                  <div className="mt-2">
                    <small>Complete Profile</small>
                  </div>
                </div>
              </div>
            </div>

            <form className="mt-5" onSubmit={handleSubmit}>
              {/* Personal Information */}
              <div className="card border border-light m-2">
                <div className="card-header border-bottom border-light">
                  <h3 className="text-center mb-1">Personal Information</h3>
                </div>

                <div className="card-body">
                  <div className="row g-2">
                    <div className="col-12 col-lg-4">
                      <div className="form-floating">
                        <input
                          id="txtFirstName"
                          type="text"
                          className="form-control"
                          placeholder="John"
                          aria-label="Enter your first name here"
                          data-type="client"
                          name="firstName"
                          value={clientInfo.firstName}
                          onChange={handleChange}
                          required
                        />
                        <label htmlFor="txtFirstName" className="form-label">
                          First Name
                        </label>
                      </div>
                    </div>
                    <div className="col-12 col-lg-4">
                      <div className="form-floating">
                        <input
                          id="txtMiddleName"
                          type="text"
                          className="form-control"
                          placeholder="Allan"
                          aria-label="Enter your middle name here (optional)"
                          data-type="client"
                          name="middleName"
                          value={clientInfo.middleName}
                          onChange={handleChange}
                        />
                        <label htmlFor="txtMiddleName" className="form-label">
                          Middle Name (Optional)
                        </label>
                      </div>
                    </div>
                    <div className="col-12 col-lg-4">
                      <div className="form-floating">
                        <input
                          id="txtLastName"
                          type="text"
                          className="form-control"
                          placeholder="Doe"
                          aria-label="Enter your last name here"
                          data-type="client"
                          name="lastName"
                          value={clientInfo.lastName}
                          onChange={handleChange}
                          required
                        />
                        <label htmlFor="txtLastName" className="form-label">
                          Last Name
                        </label>
                      </div>
                    </div>

                    <div className="col-12">
                      <div className="form-floating">
                        <input
                          id="dateDOB"
                          type="date"
                          className="form-control"
                          placeholder="01-01-2000"
                          aria-label="Enter your Date of Birth here"
                          data-type="client"
                          name="DOB"
                          value={clientInfo.DOB}
                          onChange={handleChange}
                          required
                          max={new Date().toISOString().split('T')[0]}
                        />
                        <label htmlFor="dateDOB" className="form-label">
                          Date of Birth
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Account Information */}
              <div className="card border border-light m-2">
                <div className="card-header border-bottom border-light">
                  <h3 className="text-center mb-1">Account Information</h3>
                </div>
                <div className="card-body">
                  <div className="row g-2">
                    <div className="col-12 col-lg-6">
                      <div className="form-floating">
                        <input
                          id="txtEmail"
                          type="email"
                          className="form-control"
                          placeholder="jdoe@email.com"
                          aria-label="Enter your email here"
                          autoComplete="on"
                          data-type="user"
                          name="email"
                          value={userInfo.email}
                          onChange={handleChange}
                          required
                        />
                        <label htmlFor="txtEmail" className="form-label">
                          Email
                        </label>
                      </div>
                    </div>

                    <div className="col-12 col-lg-6">
                      <div className="form-floating">
                        <input
                          id="txtUsername"
                          type="text"
                          className="form-control"
                          placeholder="johndoe"
                          aria-label="Enter your username here"
                          autoComplete="on"
                          data-type="user"
                          name="userName"
                          value={userInfo.userName}
                          onChange={handleChange}
                          required
                        />
                        <label htmlFor="txtUsername" className="form-label">
                          Username
                        </label>
                      </div>
                    </div>

                    <div className="col-12 col-lg-6">
                      <div className="form-floating">
                        <input
                          id="txtPassword"
                          type="password"
                          className="form-control"
                          placeholder="123456789"
                          aria-label="Enter your password here"
                          autoComplete="on"
                          data-type="user"
                          name="password"
                          value={userInfo.password}
                          onChange={handleChange}
                          required
                        />
                        <label htmlFor="txtPassword" className="form-label">
                          Password
                        </label>
                      </div>
                    </div>

                    <div className="col-12 col-lg-6">
                      <div className="form-floating">
                        <input
                          id="txtConfirmPassword"
                          type="password"
                          className="form-control"
                          placeholder="123456789"
                          aria-label="Confirm your password here"
                          autoComplete="on"
                          data-type="confirmPassword"
                          name="confirmPassword"
                          value={confirmPassword.confirmPassword}
                          onChange={handleChange}
                          required
                        />
                        <label
                          htmlFor="txtConfirmPassword"
                          className="form-label"
                        >
                          Confirm Password
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {errors.length > 0 && (
                <div className="alert alert-danger m-2">
                  <h5>Please fix the following errors:</h5>
                  <ul className="mb-0">
                    {errors.map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                  </ul>
                </div>
              )}

              <button
                id="btnRegister"
                type="submit"
                className="btn btn-primary col-12"
              >
                Next
              </button>
            </form>

            <div className="col-12 d-flex justify-content-center align-items-center mt-2">
              <div className="col-12 row g-2">
                <Link
                  to="/signin"
                  className="text-center text-lg-start col-12 col-lg-6"
                >
                  Have a LogIn? Click here to sign in.
                </Link>
                <Link
                  to="/"
                  className="text-center text-lg-end col-12 col-lg-6"
                >
                  Go back to home
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      <footer className="footer">
        <div className="footer-body d-flex justify-content-between mx-4 pb-3">
          <ul className="list-inline mb-0 p-0">
            <li className="list-inline-item">
              <a href="./dashboard/extra/privacy-policy.html">Privacy Policy</a>
            </li>
            <li className="list-inline-item">
              <a href="./dashboard/extra/terms-of-service.html">Terms of Use</a>
            </li>
          </ul>
          <div className="right-panel">
            ©<script>document.write(new Date().getFullYear())</script> Bridging
            Hope. All Rights Reserved.
          </div>
        </div>
      </footer>
    </React.Fragment>
  );
};

export default Register;